import { BUZON_CASOS } from '../../../lib/modulos';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';
import { useEditor, detalleError } from './contexto';
import { FilaInterruptor, Selector } from './controles';

/** De ISO (UTC) al valor de <input type="datetime-local"> en la hora de quien edita. */
function aLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Tarjeta({ titulo, descripcion, children, plegable = false, abiertaInicial = true }) {
  const [abierta, setAbierta] = useState(abiertaInicial);
  return (
    <section className="fe-config-tarjeta">
      <header
        className={`fe-config-cabeza ${plegable ? 'is-plegable' : ''}`}
        onClick={plegable ? () => setAbierta((v) => !v) : undefined}
      >
        <div>
          <h2>{titulo}</h2>
          {descripcion && <p>{descripcion}</p>}
        </div>
        {plegable && (
          <button type="button" className="fe-icono-btn" aria-expanded={abierta} aria-label={abierta ? 'Contraer' : 'Expandir'}>
            <ChevronDown className={abierta ? 'fe-girar' : ''} />
          </button>
        )}
      </header>
      {abierta && <div className="fe-config-cuerpo">{children}</div>}
    </section>
  );
}

/**
 * Pestaña Configuración, con las tarjetas de Google Forms (cuestionario, respuestas, presentación,
 * valores predeterminados) más la de publicación y cierre. Los ajustes van por `cambios` (se guardan
 * con el resto del formulario); publicación y cierre, por su ruta propia.
 */
export default function PestanaConfiguracion() {
  const { formulario, puedeEditar, setCampo, onPublicacion, onBuzon } = useEditor();
  const a = formulario.ajustes || {};
  const q = a.cuestionario || {};
  const pub = formulario.publicacion || {};
  const d = !puedeEditar;

  const setAjustes = (campos, opciones) => setCampo('ajustes', { ...a, ...campos }, { historial: false, ...opciones });
  const setCuestionario = (campos) => setAjustes({ cuestionario: { ...q, ...campos } });

  const [mensajeCerrado, setMensajeCerrado] = useState(pub.mensaje_cerrado || '');
  const [limite, setLimite] = useState(pub.limite_respuestas ?? '');
  useEffect(() => setMensajeCerrado(pub.mensaje_cerrado || ''), [pub.mensaje_cerrado]);
  useEffect(() => setLimite(pub.limite_respuestas ?? ''), [pub.limite_respuestas]);

  const publicar = async (parcial, exito) => {
    try {
      await onPublicacion(parcial);
      if (exito) toast.success(exito);
    } catch (err) {
      toast.error(detalleError(err, 'No se pudo guardar'));
    }
  };

  const sinCorreo = (a.recopilar_correos || 'no') === 'no';

  return (
    <div className="fe-config fm-scroll">
      <div className="fe-config-columna">
        {BUZON_CASOS && <Tarjeta
          titulo="Buzón de casos"
          descripcion="Cada respuesta se convierte en un caso con folio, responsable, plazo y seguimiento (quejas y sugerencias, solicitudes, reportes)."
        >
          <div className="fe-fila-ajuste">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">{formulario.buzon?.activo ? 'Activado' : 'Desactivado'}</span>
              <span className="fe-fila-ajuste-desc">
                {formulario.buzon?.activo
                  ? `Folios ${formulario.buzon.prefijo || 'QS'}-00001… · ${(formulario.buzon.categorias || []).length} categorías · los casos se ven en la pestaña Casos.`
                  : 'Categorías, asignación, plazos, Aurora IA y lo que recibe quien envía.'}
              </span>
            </span>
            <button type="button" className="fe-btn-borde" onClick={onBuzon} disabled={d}>
              {formulario.buzon?.activo ? 'Configurar' : 'Activar y configurar'}
            </button>
          </div>
        </Tarjeta>}
        <Tarjeta titulo="Convertir en cuestionario" descripcion="Asigna puntos, define respuestas correctas y califica automáticamente.">
          <FilaInterruptor
            id="cfg-cuestionario"
            titulo="Convertir en cuestionario"
            checked={a.es_cuestionario}
            onChange={(v) => setAjustes({ es_cuestionario: v })}
            disabled={d}
          />
          {a.es_cuestionario && (
            <div className="fe-config-sub">
              <div className="fe-fila-ajuste">
                <span className="fe-fila-ajuste-texto">
                  <span className="fe-fila-ajuste-titulo">Publicar calificación</span>
                  <span className="fe-fila-ajuste-desc">Inmediatamente después de cada envío, o más tarde tras revisarla a mano.</span>
                </span>
                <Selector
                  value={q.publicar_calificacion || 'inmediata'}
                  onChange={(v) => setCuestionario({ publicar_calificacion: v })}
                  opciones={[
                    { valor: 'inmediata', texto: 'Inmediatamente después de cada envío' },
                    { valor: 'manual', texto: 'Más tarde, tras la revisión manual' },
                  ]}
                  etiqueta="Publicar calificación"
                  disabled={d}
                />
              </div>
              <div className="fe-config-rotulo">Quien responde puede ver</div>
              <FilaInterruptor id="cfg-falladas" titulo="Preguntas falladas" checked={q.ver_falladas !== false} onChange={(v) => setCuestionario({ ver_falladas: v })} disabled={d} />
              <FilaInterruptor id="cfg-correctas" titulo="Respuestas correctas" checked={q.ver_correctas !== false} onChange={(v) => setCuestionario({ ver_correctas: v })} disabled={d} />
              <FilaInterruptor id="cfg-puntos" titulo="Valores de puntos" checked={q.ver_puntos !== false} onChange={(v) => setCuestionario({ ver_puntos: v })} disabled={d} />
            </div>
          )}
        </Tarjeta>

        <Tarjeta titulo="Respuestas" descripcion="Gestiona cómo se recogen y protegen las respuestas." plegable>
          <div className="fe-fila-ajuste">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Recopilar direcciones de correo electrónico</span>
              <span className="fe-fila-ajuste-desc">«Verificadas» pide iniciar sesión con una cuenta de la organización.</span>
            </span>
            <Selector
              value={a.recopilar_correos || 'no'}
              onChange={(v) => setAjustes({ recopilar_correos: v, ...(v === 'no' ? { enviar_copia: 'no' } : {}) })}
              opciones={[
                { valor: 'no', texto: 'No recopilar' },
                { valor: 'verificados', texto: 'Verificadas' },
                { valor: 'escritos', texto: 'Escritas por quien responde' },
              ]}
              etiqueta="Recopilar direcciones de correo"
              disabled={d}
            />
          </div>
          <div className={`fe-fila-ajuste ${sinCorreo ? 'is-deshabilitada' : ''}`}>
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Enviar a quien responde una copia de su respuesta</span>
              <span className="fe-fila-ajuste-desc">Necesita recopilar el correo. Sale desde tu buzón.</span>
            </span>
            <Selector
              value={sinCorreo ? 'no' : (a.enviar_copia || 'no')}
              onChange={(v) => setAjustes({ enviar_copia: v })}
              opciones={[
                { valor: 'no', texto: 'Desactivado' },
                { valor: 'si_lo_pide', texto: 'Si lo solicita' },
                { valor: 'siempre', texto: 'Siempre' },
              ]}
              etiqueta="Enviar copia de la respuesta"
              disabled={d || sinCorreo}
            />
          </div>
          <FilaInterruptor
            id="cfg-editar"
            titulo="Permitir editar las respuestas"
            descripcion="Se podrán cambiar después de enviarlas, con el enlace que recibe quien responde."
            checked={a.permitir_editar}
            onChange={(v) => setAjustes({ permitir_editar: v })}
            disabled={d}
          />
          <FilaInterruptor
            id="cfg-una"
            titulo="Limitar a 1 respuesta"
            descripcion="Quien responde tendrá que iniciar sesión con su cuenta de la organización."
            checked={a.limitar_una_respuesta}
            onChange={(v) => setAjustes({ limitar_una_respuesta: v })}
            disabled={d}
          />
        </Tarjeta>

        <Tarjeta titulo="Presentación" descripcion="Gestiona cómo se presentan el formulario y las respuestas." plegable>
          <FilaInterruptor id="cfg-progreso" titulo="Mostrar barra de progreso" checked={a.barra_progreso} onChange={(v) => setAjustes({ barra_progreso: v })} disabled={d} />
          <FilaInterruptor id="cfg-orden" titulo="Orden aleatorio de las preguntas" descripcion="Dentro de cada sección." checked={a.orden_aleatorio} onChange={(v) => setAjustes({ orden_aleatorio: v })} disabled={d} />
          <div className="fe-fila-ajuste fe-fila-ajuste-bloque">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Mensaje de confirmación</span>
            </span>
            <textarea
              className="fe-area"
              rows={2}
              value={a.mensaje_confirmacion || ''}
              placeholder="Se registró tu respuesta."
              onChange={(e) => setAjustes({ mensaje_confirmacion: e.target.value })}
              aria-label="Mensaje de confirmación"
              disabled={d}
            />
          </div>
          <FilaInterruptor id="cfg-otra" titulo="Mostrar enlace para enviar otra respuesta" checked={a.enlace_otra_respuesta !== false} onChange={(v) => setAjustes({ enlace_otra_respuesta: v })} disabled={d} />
          <FilaInterruptor id="cfg-resumen" titulo="Ver resumen de resultados" descripcion="Quien responde verá las gráficas y respuestas de todos (sin correos ni archivos)." checked={a.ver_resumen} onChange={(v) => setAjustes({ ver_resumen: v })} disabled={d} />
          <FilaInterruptor id="cfg-borrador" titulo="Guardar el progreso de quien responde" descripcion="Si cierra la pestaña, lo escrito sigue ahí al volver en el mismo navegador." checked={a.borrador_automatico !== false} onChange={(v) => setAjustes({ borrador_automatico: v })} disabled={d} />
        </Tarjeta>

        <Tarjeta titulo="Recepción de respuestas" descripcion="Cierra el formulario a mano, en una fecha o al llegar a un número de respuestas." plegable>
          <FilaInterruptor
            id="cfg-acepta"
            titulo="Aceptar respuestas"
            checked={pub.acepta_respuestas !== false}
            onChange={(v) => publicar({ acepta_respuestas: v }, v ? 'El formulario acepta respuestas' : 'El formulario ya no acepta respuestas')}
            disabled={d}
          />
          <div className="fe-fila-ajuste fe-fila-ajuste-bloque">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Mensaje para quien llegue con el formulario cerrado</span>
            </span>
            <textarea
              className="fe-area"
              rows={2}
              value={mensajeCerrado}
              placeholder="Este formulario ya no acepta respuestas."
              onChange={(e) => setMensajeCerrado(e.target.value)}
              onBlur={() => mensajeCerrado !== (pub.mensaje_cerrado || '') && publicar({ mensaje_cerrado: mensajeCerrado })}
              aria-label="Mensaje del formulario cerrado"
              disabled={d}
            />
          </div>
          <div className="fe-fila-ajuste">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Cerrar automáticamente</span>
              <span className="fe-fila-ajuste-desc">Fecha y hora en tu zona horaria.</span>
            </span>
            <span className="fe-fila-controles">
              <input
                type="datetime-local"
                className="fe-input-caja"
                value={aLocal(pub.cierra_en)}
                onChange={(e) => {
                  const v = e.target.value;
                  publicar({ cierra_en: v ? new Date(v).toISOString() : null });
                }}
                aria-label="Cerrar automáticamente el"
                disabled={d}
              />
              {pub.cierra_en && (
                <button type="button" className="fe-btn-texto" onClick={() => publicar({ cierra_en: null })} disabled={d}>Quitar</button>
              )}
            </span>
          </div>
          <div className="fe-fila-ajuste">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Límite de respuestas</span>
              <span className="fe-fila-ajuste-desc">Al llegar al número, deja de aceptar respuestas. Vacío = sin límite.</span>
            </span>
            <input
              type="number"
              min="1"
              inputMode="numeric"
              className="fe-input-caja fe-input-corto"
              value={limite}
              placeholder="Sin límite"
              onChange={(e) => setLimite(e.target.value)}
              onBlur={() => {
                const n = limite === '' ? null : Math.max(1, Math.round(Number(limite)) || 1);
                if (n !== (pub.limite_respuestas ?? null)) publicar({ limite_respuestas: n });
              }}
              aria-label="Límite de respuestas"
              disabled={d}
            />
          </div>
        </Tarjeta>

        <Tarjeta titulo="Valores predeterminados" descripcion="Para las preguntas nuevas de este formulario." plegable abiertaInicial={false}>
          <FilaInterruptor
            id="cfg-obligatorias"
            titulo="Preguntas obligatorias por defecto"
            checked={a.obligatorias_por_defecto}
            onChange={(v) => setAjustes({ obligatorias_por_defecto: v })}
            disabled={d}
          />
          <div className="fe-fila-ajuste">
            <span className="fe-fila-ajuste-texto">
              <span className="fe-fila-ajuste-titulo">Valor de puntos predeterminado</span>
              <span className="fe-fila-ajuste-desc">En cuestionarios.</span>
            </span>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              className="fe-input-caja fe-input-corto"
              value={q.puntos_por_defecto ?? 0}
              onChange={(e) => setCuestionario({ puntos_por_defecto: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
              aria-label="Valor de puntos predeterminado"
              disabled={d}
            />
          </div>
        </Tarjeta>
      </div>
    </div>
  );
}
